import { type ReactNode, useEffect, useId, useState } from 'react';
import InstallPromptLocation from '@alltrails/amplitude/enums/InstallPromptLocation';
import InstallPromptTrigger from '@alltrails/amplitude/enums/InstallPromptTrigger';
import logInstallPromptDismissed from '@alltrails/amplitude/events/logInstallPromptDismissed';
import logInstallPromptViewed from '@alltrails/amplitude/events/logInstallPromptViewed';
import useIsMobileSizedScreen from '@alltrails/denali/hooks/useIsMobileSizedScreen';
import Modal from '@alltrails/modal/components/Modal/Modal';
import { ModalAriaLabelledByProvider } from '@alltrails/modal/components/Modal/ModalAriaLabelledByContext';
import styles from './BaseInstallInterstitial.module.scss';

type BaseInstallInterstitialProps = {
  isOpen?: boolean;
  onRequestClose?: () => void;
  promptLocation: InstallPromptLocation;
  displayTrigger?: InstallPromptTrigger;
  children: ReactNode;
  hidePageScrollbarGutter?: boolean;
  isDeviceHeight?: boolean;
  shouldHideCloseButton?: boolean;
  clickOutsideDeactivates?: boolean;
};

const BaseInstallInterstitial = ({
  isOpen: isOpenAttempt = false,
  onRequestClose,
  promptLocation,
  displayTrigger,
  children,
  hidePageScrollbarGutter,
  isDeviceHeight = false,
  shouldHideCloseButton = false,
  clickOutsideDeactivates = true
}: BaseInstallInterstitialProps) => {
  const isMobile = useIsMobileSizedScreen();
  const [isMounted, setIsMounted] = useState(false);
  const ariaLabelledById = useId();
  const isOpen = isMounted && isOpenAttempt && isMobile;

  useEffect(() => {
    setIsMounted(true);
  }, []);

  useEffect(() => {
    if (isOpen) {
      logInstallPromptViewed({
        install_prompt_location: promptLocation,
        install_prompt_type: 'interstitial',
        install_prompt_source: 'native_with_appsflyer_link',
        install_prompt_trigger: displayTrigger
      });
    }
  }, [isOpen, promptLocation, displayTrigger]);

  const handleCloseClick = () => {
    logInstallPromptDismissed({
      install_prompt_location: promptLocation,
      install_prompt_type: 'interstitial',
      install_prompt_trigger: displayTrigger,
      install_prompt_source: 'native_with_appsflyer_link'
    });
    onRequestClose?.();
  };

  return (
    <Modal
      ariaLabelElementId={ariaLabelledById}
      contentsClassName={styles.contents}
      focusTrapOptions={{ clickOutsideDeactivates, escapeDeactivates: clickOutsideDeactivates }}
      hidePageScrollbarGutter={hidePageScrollbarGutter}
      isDeviceHeight={isDeviceHeight}
      isOpen={isOpen}
      onCloseRequest={handleCloseClick}
      shouldHideCloseButton={shouldHideCloseButton}
    >
      <ModalAriaLabelledByProvider ariaLabelledBy={ariaLabelledById}>{children}</ModalAriaLabelledByProvider>
    </Modal>
  );
};

export default BaseInstallInterstitial;
